// Calendar-month arithmetic for ClubWise uploads, on plain YYYY-MM-DD / YYYY-MM
// strings. No Date-local behaviour: every conversion goes through UTC so a
// browser in any zone gets the same answer as the server.

const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isMonthKey(s: string): boolean {
  return MONTH_RE.test(s);
}

export function monthKeyOfDay(day: string): string {
  return day.slice(0, 7);
}

export function monthFirstDay(key: string): string {
  return `${key}-01`;
}

export function monthLastDay(key: string): string {
  const m = MONTH_RE.exec(key);
  if (!m) throw new Error(`Invalid month key: ${key}`);
  const last = new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate();
  return `${key}-${String(last).padStart(2, '0')}`;
}

function nextMonth(key: string): string {
  const m = MONTH_RE.exec(key);
  if (!m) throw new Error(`Invalid month key: ${key}`);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`;
}

/**
 * The calendar months that lie WHOLLY inside [from, to] (both inclusive).
 *
 * ⚠️ This is the rule leads obey: ClubWise lead exports carry no lead date, only
 * the month they were run for, so a lead figure is only meaningful for a whole
 * month. A part-month range includes NO month it does not fully cover.
 */
export function wholeMonthsIn(from: string, to: string): string[] {
  if (!DAY_RE.test(from) || !DAY_RE.test(to) || from > to) return [];
  const out: string[] = [];
  let key = from.slice(0, 7);
  // First month is whole only if the range starts on its 1st.
  if (from !== monthFirstDay(key)) key = nextMonth(key);
  for (let guard = 0; guard < 600 && monthLastDay(key) <= to; guard++) {
    out.push(key);
    key = nextMonth(key);
  }
  return out;
}

/** Every month key from `fromKey` to `toKey` inclusive. */
export function monthsBetween(fromKey: string, toKey: string): string[] {
  if (!isMonthKey(fromKey) || !isMonthKey(toKey) || fromKey > toKey) return [];
  const out: string[] = [];
  let key = fromKey;
  for (let guard = 0; guard < 600 && key <= toKey; guard++) {
    out.push(key);
    key = nextMonth(key);
  }
  return out;
}

/** A dd/mm/yyyy ClubWise date → YYYY-MM-DD, or null if it is not a real date. */
export function parseClubwiseDate(raw: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw.trim());
  if (!m) return null;
  const d = Number(m[1]);
  const mo = Number(m[2]);
  const y = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
